import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
  noIndex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image = 'https://images.pexels.com/photos/8867431/pexels-photo-8867431.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630&dpr=1',
  url,
  type = 'website',
  structuredData,
  noIndex = false
}) => {
  const { language } = useLanguage();
  
  const baseUrl = 'https://laimu.mx';
  const currentUrl = url ? `${baseUrl}${url}` : baseUrl;
  
  const defaultTitles = {
    en: 'Cloud ERP Mexico | Manufacturing Management System | LAIMU',
    es: 'ERP en la Nube México | Sistema Gestión Manufactura | LAIMU'
  };

  const defaultDescriptions = {
    en: 'Mexico #1 cloud ERP for manufacturing. CFDI 4.0, Carta Porte, integrated payroll. 500+ companies trust LAIMU. Free demo available.',
    es: 'Software ERP en la nube #1 para manufactura en México. CFDI 4.0, Carta Porte, nómina integrada. +500 empresas confían en LAIMU. Demo gratuita.'
  };

  const defaultKeywords = {
    en: 'cloud ERP Mexico, manufacturing management system, payroll software Mexico, ERP manufacturing Mexico, CFDI 4.0, Carta Porte, ERP Monterrey, automotive software',
    es: 'ERP en la nube México, sistema gestión manufactura, software nómina México, ERP manufactura México, CFDI 4.0, Carta Porte, ERP Monterrey, software automotriz'
  };

  const pageTitle = title || defaultTitles[language];
  const pageDescription = description || defaultDescriptions[language];
  const pageKeywords = keywords || defaultKeywords[language];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="LAIMU" />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="language" content={language} />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="format-detection" content="telephone=yes" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Alternate Language URLs */}
      <link rel="alternate" hrefLang="es-MX" href={currentUrl} />
      <link rel="alternate" hrefLang="en" href={currentUrl} />
      <link rel="alternate" hrefLang="x-default" href={currentUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="LAIMU Trantor" />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : 'es_MX'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@LaimuTrantor" />
      <meta name="twitter:creator" content="@LaimuTrantor" />
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#115d7b" />
      <meta name="msapplication-TileColor" content="#115d7b" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="LAIMU Trantor" />
      
      {/* Geo Meta Tags */}
      <meta name="geo.region" content="MX" />
      <meta name="geo.placename" content="Monterrey" />
      <meta name="geo.position" content="25.6714;-100.309" />
      <meta name="ICBM" content="25.6714, -100.309" />
      
      {/* Business Meta Tags */}
      <meta name="price" content="Contact for pricing" />
      <meta name="priceCurrency" content="MXN" />
      <meta name="availability" content="InStock" />
      <meta name="condition" content="NewCondition" />
      <meta name="rating" content="5" />
      <meta name="distribution" content="global" />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;